import type { ICouponListParams, ICreateCouponBody, IUpdateCouponBody } from "@/api/types/coupons";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    GetCoupons,
    GetCouponById,
    CreateCoupon,
    UpdateCoupon,
    DeleteCoupon,
    RevokeCoupon,
    GenerateCouponCode,
    DownloadCouponCodes,
    ToggleCoupon,
} from "@/api/services/coupons";

export function useGetCouponById(id: number) {
    return useQuery({
        queryKey: ["coupon", id],
        queryFn: () => GetCouponById(id),
        enabled: id > 0,
    });
}

export function useGetCoupons(params: ICouponListParams, extraQuery?: string) {
    return useQuery({
        queryKey: ["coupons", params, extraQuery],
        queryFn: () => GetCoupons(params, extraQuery),
    });
}

export function useCreateCoupon() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: ICreateCouponBody) => CreateCoupon(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["coupons"] });
        },
    });
}

export function useUpdateCoupon() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IUpdateCouponBody }) => UpdateCoupon(id, body),
        onSuccess: (data, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["coupon", id] });
            queryClient.invalidateQueries({ queryKey: ["coupons"] });
        },
    });
}

export function useDeleteCoupon() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => DeleteCoupon(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ["coupon", id] });
            queryClient.invalidateQueries({ queryKey: ["coupons"] });
        },
    });
}

export function useRevokeCoupon() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: { revokeReason?: string } }) => RevokeCoupon(id, body),
        onSuccess: (data, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["coupon", id] });
            queryClient.invalidateQueries({ queryKey: ["coupons"] });
        },
    });
}

export function useGenerateCouponCode() {
    return useMutation({
        mutationFn: (count: number) => GenerateCouponCode(count),
    });
}

export function useDownloadCouponCodes() {
    return useMutation({
        mutationFn: (groupId: number) => DownloadCouponCodes(groupId),
    });
}

export function useToggleCoupon() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => ToggleCoupon(id),
        onSuccess: (data, id) => {
            queryClient.invalidateQueries({ queryKey: ["coupon", id] });
            queryClient.invalidateQueries({ queryKey: ["coupons"] });
        },
    });
}
